/**
 * Scrollbar geometry (SRS C2, docs/06-UI-SPEC.md §6.7).
 *
 * An infinite canvas has no edges, so the scrollbars measure the only thing that has
 * any size: the work that has been drawn, plus wherever the teacher has scrolled to.
 * The thumb is the share of that which is on screen — the same promise a scrollbar
 * makes anywhere else.
 */

import { clamp, type Rect, type Viewport } from '@opal/shared';

/** Never let the thumb shrink to something too small to grab (docs/07 §9, 44 px). */
const MIN_THUMB_RATIO = 0.08;

export interface AxisScroll {
  /** Share of the track the thumb covers, 0–1. */
  readonly thumbRatio: number;
  /** How far along the free part of the track the thumb sits, 0–1. */
  readonly offsetRatio: number;
  /** Canvas units the board can still be scrolled along this axis. */
  readonly scrollableCanvas: number;
}

export interface ScrollbarState {
  readonly horizontal: AxisScroll;
  readonly vertical: AxisScroll;
}

function axis(
  viewStart: number,
  viewSpan: number,
  contentStart: number,
  contentSpan: number,
): AxisScroll {
  const start = Math.min(viewStart, contentStart);
  const end = Math.max(viewStart + viewSpan, contentStart + contentSpan);
  const total = Math.max(end - start, viewSpan);
  const scrollable = Math.max(total - viewSpan, 0);

  return {
    thumbRatio: clamp(viewSpan / total, MIN_THUMB_RATIO, 1),
    offsetRatio: scrollable === 0 ? 0 : clamp((viewStart - start) / scrollable, 0, 1),
    scrollableCanvas: scrollable,
  };
}

/** Where both thumbs sit, and how big they are, for the current view. */
export function scrollbarState(viewport: Viewport, content: Rect | null): ScrollbarState {
  const visibleWidth = viewport.widthPx / viewport.zoom;
  const visibleHeight = viewport.heightPx / viewport.zoom;
  const box = content ?? {
    x: viewport.panX,
    y: viewport.panY,
    width: visibleWidth,
    height: visibleHeight,
  };

  return {
    horizontal: axis(viewport.panX, visibleWidth, box.x, box.width),
    vertical: axis(viewport.panY, visibleHeight, box.y, box.height),
  };
}

/**
 * Canvas distance to scroll when a thumb is dragged along its track.
 *
 * The thumb only travels the part of the track it does not itself cover, so that
 * shorter distance is what maps onto the whole scrollable range.
 */
export function dragToCanvas(dragPx: number, trackPx: number, scroll: AxisScroll): number {
  const freeTrack = trackPx * (1 - scroll.thumbRatio);
  if (freeTrack <= 0 || scroll.scrollableCanvas === 0) return 0;
  return (dragPx / freeTrack) * scroll.scrollableCanvas;
}
